/**
 * Zakres dat: cztery presety i kalendarz na wszystko poza nimi.
 *
 * Presety odpowiadają na pytanie „jak jest teraz". Kalendarz odpowiada na
 * pytanie „co się stało wtedy": tydzień kampanii, doba po awarii, kwartał do
 * porównania z tym samym kwartałem rok wcześniej. Bez niego jedyną drogą do
 * takiego okresu było wybranie presetu, który go obejmuje, i odejmowanie
 * reszty w pamięci.
 *
 * Wartość jest jednym stringiem — `30d` albo `2026-03-01:2026-03-14` — i w tej
 * samej postaci jedzie do API jako `?range=`. Dzięki temu klucze React Query,
 * cache i walidacja po stronie backendu mają jeden kształt do obsłużenia
 * zamiast trzech parametrów, które da się ustawić sprzecznie.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

const PRESETS = ["7d", "30d", "90d", "365d"];

/** Musi zgadzać się z `MAX_RANGE_DAYS` w `app/api/v1/dashboard.py`. */
const MAX_DAYS = 1830;

/** Tydzień zaczyna się w poniedziałek — kalendarz ma być polski, nie domyślny. */
const WEEKDAYS: ReadonlyArray<readonly [string, string]> = [
  ["pn", "poniedziałek"],
  ["wt", "wtorek"],
  ["śr", "środa"],
  ["cz", "czwartek"],
  ["pt", "piątek"],
  ["sb", "sobota"],
  ["nd", "niedziela"],
];

const MONTH = new Intl.DateTimeFormat("pl-PL", { month: "long", year: "numeric" });
const SHORT = new Intl.DateTimeFormat("pl-PL", { day: "2-digit", month: "2-digit" });
const FULL = new Intl.DateTimeFormat("pl-PL", { day: "numeric", month: "long", year: "numeric" });

/* ── Daty jako `RRRR-MM-DD` w czasie lokalnym ─────────────────────────────── */

/* Kalendarz operuje na stringach, nie na obiektach `Date`, bo porównanie
   `"2026-03-01" < "2026-03-14"` daje dokładnie to samo co porównanie dat i nie
   ma w sobie strefy czasowej, o którą można się potknąć. `toISOString()` jest
   tu celowo nieużywany: przelicza na UTC i w Warszawie potrafi cofnąć wybrany
   dzień o jeden. */

function iso(value: Date) {
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
}

function fromIso(value: string) {
  return new Date(
    Number(value.slice(0, 4)),
    Number(value.slice(5, 7)) - 1,
    Number(value.slice(8, 10)),
  );
}

function shift(value: string, days: number) {
  const moved = fromIso(value);
  moved.setDate(moved.getDate() + days);
  return iso(moved);
}

/** Liczba dni w zakresie, obie daty włącznie — tak liczy go backend. */
function span(start: string, end: string) {
  return Math.round((fromIso(end).getTime() - fromIso(start).getTime()) / 86_400_000) + 1;
}

function monthOf(value: string) {
  return `${value.slice(0, 7)}-01`;
}

function addMonths(month: string, delta: number) {
  const moved = fromIso(month);
  moved.setMonth(moved.getMonth() + delta);
  return iso(moved);
}

function isCustom(value: string) {
  return value.includes(":");
}

/** Rozkłada `2026-03-01:2026-03-14` na obie daty. */
function ends(value: string): [string, string] {
  const colon = value.indexOf(":");
  return [value.slice(0, colon), value.slice(colon + 1)];
}

function countDays(days: number) {
  return days === 1 ? "1 dzień" : `${days} dni`;
}

function label(value: string) {
  const [start, end] = ends(value);
  const from = SHORT.format(fromIso(start));
  return start === end ? from : `${from}–${SHORT.format(fromIso(end))}`;
}

function describe(value: string) {
  const [start, end] = ends(value);
  return `${FULL.format(fromIso(start))} – ${FULL.format(fromIso(end))} (${countDays(
    span(start, end),
  )})`;
}

/** Punkt wyjścia dla kalendarza: zakres własny wprost, preset po przeliczeniu. */
function seed(value: string, today: string): { start: string; end: string | null } {
  if (isCustom(value)) {
    const [start, end] = ends(value);
    return { start, end };
  }
  const days = Number(value.replace("d", "")) || 30;
  return { start: shift(today, 1 - days), end: today };
}

/* ── Kontrolka ────────────────────────────────────────────────────────────── */

export function RangePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const field = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const custom = isCustom(value);

  const close = useCallback(() => {
    setOpen(false);
    trigger.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    // Kliknięcie poza kalendarzem zamyka go bez zabierania ogniska — patrzący
    // właśnie wskazał, gdzie chce być.
    const onPointerDown = (event: PointerEvent) => {
      if (!field.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open, close]);

  return (
    <div className="rangefield" ref={field}>
      <div className="rangepicker" role="group" aria-label="Zakres dat">
        {PRESETS.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => {
              onChange(option);
              setOpen(false);
            }}
          >
            {option}
          </button>
        ))}
        <span className="rangepicker__sep" aria-hidden="true" />
        <button
          type="button"
          ref={trigger}
          aria-pressed={custom}
          aria-haspopup="dialog"
          aria-expanded={open}
          title={custom ? describe(value) : "Dowolny zakres z kalendarza"}
          onClick={() => (open ? close() : setOpen(true))}
        >
          {custom ? label(value) : "zakres…"}
        </button>
      </div>

      {open && (
        <Calendar
          value={value}
          onApply={(next) => {
            onChange(next);
            close();
          }}
          onCancel={close}
        />
      )}
    </div>
  );
}

/* ── Kalendarz ────────────────────────────────────────────────────────────── */

const STEPS: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
  PageUp: -28,
  PageDown: 28,
};

function Calendar({
  value,
  onApply,
  onCancel,
}: {
  value: string;
  onApply: (next: string) => void;
  onCancel: () => void;
}) {
  const today = iso(new Date());
  const [range, setRange] = useState(() => seed(value, today));
  const [cursor, setCursor] = useState(range.end ?? range.start);
  const [month, setMonth] = useState(() => monthOf(range.end ?? range.start));

  const grid = useRef<HTMLTableElement>(null);

  /**
   * Czy ognisko ma iść za kursorem. Przy otwarciu i przy strzałkach tak — bez
   * tego przejście strzałką do poprzedniego miesiąca gubi ognisko, bo React
   * wymienia całą siatkę i przycisk, na którym ono stało, przestaje istnieć.
   * Przy wpisywaniu daty w pole „Od"/„Do" nie: ognisko musi zostać w polu.
   */
  const followCursor = useRef(true);

  useEffect(() => {
    if (!followCursor.current) return;
    grid.current?.querySelector<HTMLButtonElement>(`[data-day="${cursor}"]`)?.focus();
  }, [cursor]);

  const weeks = useMemo(() => {
    const first = fromIso(month);
    const blanks = (first.getDay() + 6) % 7;
    const length = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();

    const cells: (string | null)[] = Array<string | null>(blanks).fill(null);
    for (let day = 1; day <= length; day += 1) {
      cells.push(iso(new Date(first.getFullYear(), first.getMonth(), day)));
    }
    while (cells.length % 7 !== 0) cells.push(null);

    return Array.from({ length: cells.length / 7 }, (_, week) =>
      cells.slice(week * 7, week * 7 + 7),
    );
  }, [month]);

  /**
   * Pierwsze kliknięcie otwiera nowy zakres, drugie go domyka. Kliknięcie
   * przed początkiem nie jest błędem — po prostu wskazuje początek.
   */
  function pick(day: string) {
    setCursor(day);
    setRange((prev) => {
      if (prev.end !== null) return { start: day, end: null };
      return day < prev.start ? { start: day, end: prev.start } : { start: prev.start, end: day };
    });
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLTableElement>) {
    const step = STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = shift(cursor, step);
    if (next > today) return;
    followCursor.current = true;
    setCursor(next);
    setMonth(monthOf(next));
  }

  function typeStart(next: string) {
    if (!next || next > today) return;
    followCursor.current = false;
    setRange((prev) => ({
      start: next,
      end: prev.end !== null && prev.end < next ? next : prev.end,
    }));
    setCursor(next);
    setMonth(monthOf(next));
  }

  function typeEnd(next: string) {
    if (!next || next > today) return;
    followCursor.current = false;
    setRange((prev) =>
      next < prev.start ? { start: next, end: prev.start } : { start: prev.start, end: next },
    );
    setCursor(next);
    setMonth(monthOf(next));
  }

  const days = range.end === null ? null : span(range.start, range.end);
  const tooLong = days !== null && days > MAX_DAYS;

  return (
    <div className="daterange" role="dialog" aria-label="Zakres własny">
      <header className="daterange__head">
        <button
          type="button"
          className="daterange__nav"
          aria-label="Poprzedni miesiąc"
          onClick={() => setMonth(addMonths(month, -1))}
        >
          ‹
        </button>
        <strong>{MONTH.format(fromIso(month))}</strong>
        <button
          type="button"
          className="daterange__nav"
          aria-label="Następny miesiąc"
          disabled={month >= monthOf(today)}
          onClick={() => setMonth(addMonths(month, 1))}
        >
          ›
        </button>
      </header>

      <table className="daterange__grid" ref={grid} onKeyDown={onKeyDown}>
        <thead>
          <tr>
            {WEEKDAYS.map(([short, full]) => (
              <th key={short} scope="col" abbr={full}>
                {short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, index) => (
            <tr key={week.find(Boolean) ?? index}>
              {week.map((day, cell) => {
                if (day === null) return <td key={cell} />;

                const isStart = day === range.start;
                const isEnd = day === (range.end ?? range.start);
                const inside = range.end !== null && day > range.start && day < range.end;
                const classes = ["daterange__day"];
                if (isStart) classes.push("daterange__day--start");
                if (isEnd) classes.push("daterange__day--end");
                if (inside) classes.push("daterange__day--range");

                return (
                  <td key={day} className={classes.join(" ")}>
                    <button
                      type="button"
                      data-day={day}
                      tabIndex={day === cursor ? 0 : -1}
                      disabled={day > today}
                      aria-label={FULL.format(fromIso(day))}
                      aria-pressed={isStart || isEnd || inside}
                      aria-current={day === today ? "date" : undefined}
                      onClick={() => pick(day)}
                    >
                      {Number(day.slice(8))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="daterange__fields">
        <label>
          Od
          <input
            type="date"
            value={range.start}
            max={today}
            onChange={(event) => typeStart(event.target.value)}
          />
        </label>
        <label>
          Do
          <input
            type="date"
            value={range.end ?? ""}
            max={today}
            onChange={(event) => typeEnd(event.target.value)}
          />
        </label>
      </div>

      {tooLong && (
        <p className="daterange__warn">
          Zakres ma {days} dni, a backend policzy najwyżej {MAX_DAYS} — przesuń początek bliżej.
        </p>
      )}

      <footer className="daterange__foot">
        <span className="daterange__span">
          {days === null ? "wskaż koniec zakresu" : countDays(days)}
        </span>
        <div className="daterange__actions">
          <button type="button" className="btn btn--quiet" onClick={onCancel}>
            Anuluj
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={days === null || tooLong}
            onClick={() => onApply(`${range.start}:${range.end}`)}
          >
            Pokaż zakres
          </button>
        </div>
      </footer>
    </div>
  );
}
